import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { useEffect, useRef, useState } from "react";
import { Fetch, ILazyParams } from "../../shared/Fetch";
import { Search } from "../../shared/Search";
import { IOpenForm } from "./CustomerProgressReport";
import { format } from "date-fns";
import { IFilter } from "./Filter";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";

interface Iprops {
    setModal: (modal: IOpenForm) => void,
    toast: any,
    reload: boolean,
    reloadData: () => void,
    hasPermission: (pageName: string, featureName?: string) => boolean,
    filter?: IFilter
}

export const Table = (props: Iprops) => {

    const [tableData, setTableData] = useState({ data: [], count: 0, loading: false } as { data: any[], count: number, loading: boolean });
    const [text, setText] = useState<string | null>(null);
    const dt = useRef<any>(null);
    const [lazyParams, setLazyParams] = useState<ILazyParams>({
        first: 0,
        rows: 25,
        page: 1,
        sortField: 'id',
        sortOrder: -1
    });
    const [detailReport, setDetailReport] = useState({ data: [] as any[], count: 0, loading: false, open: false, title: "" });
    const [detailLazyParams, setDetailLazyParams] = useState<ILazyParams>({
        first: 0,
        rows: 25,
        page: 1,
        sortField: 'id',
        sortOrder: -1
    });
    const [selectedDetailParams, setSelectedDetailParams] = useState({ contact_ID: 0, status: "" });

    useEffect(() => {
        loadTableData();
    }, [text, lazyParams, props.reload, props.filter]);

    const onPage = (event: any) => {
        delete event.filters;
        delete event.multiSortMeta;
        setLazyParams(event);
    }

    const onSort = (event: any) => {
        delete event.filters;
        delete event.multiSortMeta;
        setLazyParams(event);
    }

    const exportCSV = () => {
        dt.current.exportCSV();
    };

    
    const loadTableData = async () => {
        setTableData((prev) => ({ ...prev, loading: true, }));
        const filterParams = props.filter ? `&filter=${JSON.stringify(props.filter)}` : '';
        const response = await Fetch("customer/Report", "GET", null, `lazyParams=${JSON.stringify(lazyParams)}${text ? '&text=' + text : ''}${filterParams}`, props.toast);
        if (response) {
            const data: { devices: any[], count: number } = await response.json();
            setTableData({ data: data.devices, count: data.count, loading: false });
        } else {
            setTableData((prev) => ({ ...prev, loading: false, }));
        }
    }

    const loadDetailData = async (contact_ID: number, status: string, lazy: ILazyParams) => {
        setDetailReport((prev) => ({ ...prev, loading: true }));
        const currentFilter = { ...props.filter, contact_ID: contact_ID, plan_status: status };
        const response = await Fetch("customer/DetailReport", "GET", null, `lazyParams=${JSON.stringify(lazy)}&filter=${JSON.stringify(currentFilter)}`, props.toast);
        if (response) {
            const data: { leads: any[], count: number } = await response.json();
            setDetailReport((prev) => ({ ...prev, data: data.leads, count: data.count, loading: false }));
        } else {
            setDetailReport((prev) => ({ ...prev, loading: false }));
        }
    }

    useEffect(() => {
        if (detailReport.open) {
            loadDetailData(selectedDetailParams.contact_ID, selectedDetailParams.status, detailLazyParams);
        }
    }, [detailReport.open, detailLazyParams, selectedDetailParams]);

    const handleCountClick = (rowData: any, status: string, title: string) => {
        const contact_ID = rowData.id; // Using id as suggested by dataKey="id"
        setSelectedDetailParams({ contact_ID, status });
        setDetailReport({ ...detailReport, open: true, title: `${title} - ${rowData.customerReport}`, data: [], loading: true });
        setDetailLazyParams({ ...detailLazyParams, first: 0, page: 1 });
    }

    const planstatusbackground = (rowData: any) => {
        const hexColor = rowData.plancolor?.startsWith('#') ? rowData.plancolor : `#${rowData.plancolor}`;
        const eaDate = rowData.expectedActivationDate ? new Date(rowData.expectedActivationDate) : null;
        const today = new Date();
        if (!eaDate || eaDate > today) {
            return <span style={{ padding: '4px 8px', borderRadius: '4px', display: 'inline-block', minWidth: '80px', textAlign: 'center' }}>Not Activated</span>;
        }

        return (
            <span
                style={{
                    backgroundColor: hexColor,
                    color: '#fff',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    display: 'inline-block',
                    minWidth: '80px',
                    textAlign: 'center'
                }}
            >
                {rowData.plan_status_label}
            </span>
        );
    };

    const planExpiredIn = (rowData: any) => {
        const days = rowData.daysRemaining;
        const eaDate = rowData.expectedActivationDate ? new Date(rowData.expectedActivationDate) : null;
        const today = new Date();

        let severity: "success" | "info" | "warning" | "danger" = "info";
        let label = "-";

        if (!eaDate || eaDate > today) {
            label = "Not Activated Yet";
        } else {
            if (days < 0) {
                severity = "danger";
                label = `Last Recharge ${Math.abs(days)} Days ago`;
            } else if (days >= 15) {
                severity = "info";
                label = `${days} Days`;
            } else if (days >= 0 && days < 15) {
                severity = "warning";
                label = `${days} Days`;
            }
        }

        return <Tag icon="pi pi-circle" severity={severity} value={label} style={{ width: '150px' }} />;
    };


    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-4">
            <div className="flex gap-4 w-full">

                <div className="w-full flex align-items-center justify-content-center">
                    <h4 style={{ margin: 0, textTransform: 'uppercase', fontFamily: 'Rancho', letterSpacing: 1 }}>Customer Progress Report</h4>
                </div>
            </div>
            <div className="flex gap-4">
                {/* <div className="p-input-icon-left">
                    <Search setSearch={setText} />
                </div> */}
                {props.hasPermission("Source", "Export") && (
                    <Button style={{ width: '80px', height: '30px' }} label="Export" icon="pi pi-upload" className="p-button-sm p-button-rounded p-button-help" onClick={exportCSV} />
                )}
            </div>
        </div>
    );



    const calculateTotal = (field: string) => {
        let total = 0;
        if (tableData.data) {
            tableData.data.forEach((item: any) => {
                if (field === 'total') {
                    total += (item.active || 0) +
                        (item.inactive || 0) +
                        (item.expired || 0) +
                        (item.criticallyAboutExpire || 0) +
                        (item.aboutToExpired || 0) +
                        (item.notActivate || 0);
                } else {
                    total += (item[field] || 0);
                }
            });
        }
        return total;
    };

    return (
        <>
            <DataTable style={{ border: '1px solid rgba(105,105,105,0.17)', borderTopWidth: "0" }}
                ref={dt} size="small" stripedRows scrollable scrollHeight="75vh" exportFilename={format(new Date(), 'dd-MM-yyyy hh_mm_ss a').toUpperCase()}
                value={tableData.data} lazy dataKey="id"
                paginator first={lazyParams.first} rows={lazyParams.rows} rowsPerPageOptions={[25, 50, 100, 200]}
                totalRecords={tableData.count} onPage={onPage}
                onSort={onSort} sortField={lazyParams.sortField} sortOrder={lazyParams.sortOrder}
                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                currentPageReportTemplate="Showing {first} to {last} of {totalRecords}"
                header={header}
                loading={tableData.loading}
            >
                <Column header="SN." body={(data, options) => options.rowIndex + 1} footer="TOTAL" />
                <Column field="customerReport" header="Customer" />
                <Column field="customernumber" header="Phone Number" />
                {/* <Column field="customerEmail" header="Customer Email"  /> */}

                <Column field="total" header="Total Plan"
                    body={(data) => {
                        const total = (data.active || 0) + (data.inactive || 0) + (data.expired || 0) + (data.criticallyAboutExpire || 0) + (data.aboutToExpired || 0) + (data.notActivate || 0);
                        return <span className="text-primary font-bold cursor-pointer underline" onClick={() => handleCountClick(data, "All", "Total Plans")}>{total}</span>
                    }}
                    footer={calculateTotal('total')}
                />
                <Column field="active" header="Active"
                    body={(data) => <span className="text-primary font-bold cursor-pointer underline" onClick={() => handleCountClick(data, "Active", "Active Plans")}>{data.active || 0}</span>}
                    footer={calculateTotal('active')}
                />
                <Column field="inactive" header="Inactive"
                    body={(data) => <span className="text-primary font-bold cursor-pointer underline" onClick={() => handleCountClick(data, "Inactive", "Inactive Plans")}>{data.inactive || 0}</span>}
                    footer={calculateTotal('inactive')}
                />
                <Column field="criticallyAboutExpire" header="Critically About to Expire"
                    body={(data) => <span className="text-primary font-bold cursor-pointer underline" onClick={() => handleCountClick(data, "Critically About to Expire", "Critically About to Expire")}>{data.criticallyAboutExpire || 0}</span>}
                    footer={calculateTotal('criticallyAboutExpire')}
                />
                <Column field="aboutToExpired" header="About to Expired"
                    body={(data) => <span className="text-primary font-bold cursor-pointer underline" onClick={() => handleCountClick(data, "About to Expired", "About to Expired")}>{data.aboutToExpired || 0}</span>}
                    footer={calculateTotal('aboutToExpired')}
                />
                <Column field="expired" header="Expired"
                    body={(data) => <span className="text-primary font-bold cursor-pointer underline" onClick={() => handleCountClick(data, "Expired", "Expired Plans")}>{data.expired || 0}</span>}
                    footer={calculateTotal('expired')}
                />
            </DataTable>
            <ConfirmDialog />

            <Dialog visible={detailReport.open} style={{ width: '90vw' }} header={detailReport.title} modal className="p-fluid" onHide={() => setDetailReport({ ...detailReport, open: false })}>
                <DataTable value={detailReport.data} loading={detailReport.loading} size="small" stripedRows showGridlines responsiveLayout="scroll" paginator rows={10}>
                    <Column header="SN." body={(data, options) => options.rowIndex + 1} />
                    <Column field="contact_Person_Name" header="Customer" />
                    <Column field="contactContact" header="Contact" />
                    <Column field="contactEmail" header="Email" />
                    <Column field="deviceName" header="device" />
                    <Column field="device_macAddress" header="Mac Address" />
                    <Column field="planName" header="Plan Name" />
                    <Column field="planDuration" header="Plan Duration" />
                    <Column field="planCost" header="Plan Cost($)" />
                    {/* <Column field="days_remaining" header="Plan Expired In" body={planExpiredIn} /> */}
                    <Column field="plan_status_label" header="Plan Status" body={planstatusbackground} />
                </DataTable>
            </Dialog>
        </>
    );
}
